import { CHAPTERS } from '../data/site';
import ChapterShell from '../components/ChapterShell';
import LightboxImage from '../components/Lightbox';
import { MaskedLines, FadeUp, Eyebrow, Counter } from '../components/Reveal';

const C = CHAPTERS[1];
const ORANGE = '#FF7E00';

const Context = () => (
  <ChapterShell chapter={C} heroImg="/assets/book/field-bkstore.jpg" heroCaption="B.K. General Store, the format under pressure, still standing.">

    {/* QUICK COMMERCE OPENER */}
    <section className="px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="context-qc">
      <Eyebrow color={ORANGE}>02 · The Force</Eyebrow>
      <MaskedLines
        lines={['Quick commerce…', 'death coming soon?']}
        className="mt-10 text-5xl md:text-8xl font-light tracking-tighter leading-[0.95] text-ink"
      />
      <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-12">
        <FadeUp className="md:col-span-7">
          <p className="text-lg md:text-2xl font-medium leading-relaxed text-ink/85">
            Order anything in a few clicks, pay a small convenience charge, and what feels like
            magic shows up at your doorstep. And in that same moment, the person you wave at
            while receiving that order, that is the call of death for the store owner you once
            depended on.
          </p>
          <p className="mt-6 text-base md:text-lg text-ink/70 leading-relaxed">
            It sounds harsh, but that is the reality. This is not just a business number, it is a
            direct hit on livelihoods. That is not a small shift; that is a structural collapse in
            motion. The threat is real, and it is already here.
          </p>
        </FadeUp>
        <div className="md:col-span-5 grid grid-cols-1 gap-px bg-ink/15 border border-ink/15 self-start">
          {[
            { v: 200000, suffix: '', l: 'kirana stores shut in the last two years' },
            { v: 10, suffix: ' lakh+', l: 'lives touched, counting families' },
          ].map((s, i) => (
            <FadeUp key={i} delay={i * 0.08} className="bg-paper p-8 md:p-10">
              <p className="text-5xl md:text-6xl font-light tracking-tighter" style={{ color: ORANGE }} data-testid={`context-stat-${i}`}>
                {s.v > 100 ? <Counter to={s.v} /> : <Counter to={s.v} format={(v) => Math.round(v).toString()} />}{s.suffix}
              </p>
              <p className="mt-3 text-sm uppercase tracking-[0.2em] text-support">{s.l}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    {/* HOW QC WORKS */}
    <section className="bg-brand-black text-paper px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="context-system">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Eyebrow color="#FFB000">How Quick Commerce Actually Operates</Eyebrow>
          <MaskedLines
            lines={['In traditional retail,', 'the store is the experience.', 'In quick commerce, the system', 'is the experience,', 'and the store disappears.']}
            className="mt-10 text-3xl md:text-5xl font-light tracking-tight leading-[1.08]"
          />
        </div>
        <div className="lg:col-span-7">
          <ul className="divide-y divide-white/15 border-y border-white/15">
            {[
              'No customer facing store, only apps as the interface',
              'Dark stores placed near residential clusters for speed',
              'Limited, high frequency selection, not endless variety',
              'Real-time demand tracking and continuous optimization',
              'Picking and packing designed for efficiency, not experience',
              'Delivery built for minutes, not hours',
              'Decisions driven by data, not memory or relationships',
            ].map((t, i) => (
              <FadeUp key={i} delay={i * 0.04} y={14}>
                <li className="flex items-baseline gap-6 py-4 text-sm md:text-base text-paper/80">
                  <span className="text-brand-amber text-xs font-medium">{String(i + 1).padStart(2, '0')}</span>
                  {t}
                </li>
              </FadeUp>
            ))}
          </ul>
          <FadeUp delay={0.2}>
            <p className="mt-8 text-sm md:text-base text-paper/60 leading-relaxed">
              A backend-first, data driven fulfilment model competing on speed, convenience and
              reliability, the same promise local stores were built on, executed with almost
              zero friction.
            </p>
          </FadeUp>
        </div>
      </div>
    </section>

    {/* DICHOTOMY */}
    <section className="px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="context-dichotomy">
      <Eyebrow color={ORANGE}>Not a Death Certificate, An Alarming Bell</Eyebrow>
      <FadeUp delay={0.1}>
        <p className="mt-8 text-xl md:text-3xl font-medium tracking-tight leading-snug max-w-4xl">
          While urban affluent India lives on apps, the mass market, the hundreds of millions
          who actually drive India's consumption, remains anchored in the
          <span style={{ color: ORANGE }}> khata and the corner shop</span>.
        </p>
      </FadeUp>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/15 border border-ink/15">
        {[
          { h: '233M', t: 'The Mass Market', d: 'They buy small and often. With an AOV of ₹150, digital logistics costs of 18 to 22% make apps unviable. The kirana wins on micro liquidity.' },
          { h: '42M', t: 'The Affluent', d: 'Time is the scarcest commodity. They pay a premium for convenience, driving the quick commerce surge.' },
          { h: '69M', t: 'The Hybrid Segment', d: 'Hypermarkets for the monthly stock up; kiranas for the "missing ingredient" moments.' },
        ].map((x, i) => (
          <FadeUp key={i} delay={i * 0.08} className="group bg-paper p-8 md:p-10 transition-colors duration-500 hover:bg-brand-orange hover:text-paper">
            <p className="text-6xl font-light tracking-tighter">{x.h}</p>
            <p className="mt-4 text-lg font-medium">{x.t}</p>
            <p className="mt-3 text-sm leading-relaxed opacity-70">{x.d}</p>
            <p className="mt-4 text-[11px] uppercase tracking-[0.2em] opacity-50">households</p>
          </FadeUp>
        ))}
      </div>
      <FadeUp delay={0.15}>
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <LightboxImage
            src="/assets/book/three formats.png"
            alt="India's three format grocery system"
            testId="three formats-img"
            caption="India's three format grocery system, source: Redseer Research and Analysis"
            className="border border-ink/15"
          />
          <div>
            <p className="text-lg md:text-xl font-semibold leading-relaxed">
              The apps are fighting over who can burn more capital to deliver a pack of biscuits.
            </p>
            <p className="mt-5 text-base md:text-lg text-ink/70 leading-relaxed">
              The kirana wins because, apart from a zero cost marketing budget, it is a community
              hub. The store owner knows your name, your brand of tea, even which regional masala
              you prefer. While platforms scale to 10,000+ SKUs, they lack the hyperlocal
              intelligence of an owner who curates 500 to 1,500 SKUs for the 200 families on his street.
            </p>
          </div>
        </div>
      </FadeUp>
    </section>

    {/* LOYALTY */}
    <section className="px-5 md:px-10 lg:px-24 pb-24 md:pb-36" data-testid="context-loyalty">
      <div className="border border-ink/15 p-8 md:p-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Eyebrow color={ORANGE}>Customer Loyalty Still Holds</Eyebrow>
          <MaskedLines
            lines={['Discounts are rain.', 'Trust is roots.']}
            className="mt-8 text-4xl md:text-6xl font-light tracking-tighter leading-[1.02]"
          />
          <FadeUp delay={0.15}>
            <p className="mt-8 text-base md:text-lg text-ink/70 leading-relaxed">
              Discounts create short term growth. Trust, relationships and consistency are roots,
              they hold customers in place when conditions change. Without roots, even
              healthy-looking growth does not last.
            </p>
          </FadeUp>
        </div>
        <div className="lg:col-span-7 flex flex-col justify-center gap-8">
          <FadeUp delay={0.1}>
            <div className="flex h-16 md:h-20 w-full overflow-hidden border border-ink/15" data-testid="channel-bar">
              <div className="flex items-center justify-center text-[10px] md:text-xs font-medium uppercase tracking-wider text-paper" style={{ width: '8.5%', background: '#01114F' }}>8.5%</div>
              <div className="flex items-center justify-center text-[10px] md:text-xs font-medium uppercase tracking-wider text-paper" style={{ width: '23.4%', background: ORANGE }}>23.4%</div>
              <div className="flex items-center justify-center text-[10px] md:text-xs font-medium uppercase tracking-wider text-ink" style={{ width: '68.1%', background: '#FFB000' }}>68.1%</div>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2 text-xs uppercase tracking-[0.2em] text-support">
              <span>■ Exclusively online</span>
              <span>■ Only offline</span>
              <span>■ Use both channels</span>
            </div>
          </FadeUp>
          <FadeUp delay={0.18}>
            <p className="text-sm md:text-base text-ink/70 leading-relaxed">
              Offline retail has not been replaced, it has become part of a hybrid behaviour.
              On a five-point scale, offline buyers scored 4 on staying loyal to a fixed shop.
              Retailers who compete only on price burn margins; retailers who build loyalty
              buy resilience.
            </p>
          </FadeUp>
        </div>
      </div>
    </section>

    {/* FAMILY DYNAMICS */}
    <section className="px-5 md:px-10 lg:px-24 pb-24 md:pb-36" data-testid="context-family">
      <Eyebrow color={ORANGE}>Family Dynamics, The Shop They Live In</Eyebrow>
      <FadeUp delay={0.1}>
        <p className="mt-8 text-lg md:text-2xl font-medium leading-relaxed max-w-4xl text-ink/85">
          Most offline stores are not just shops, they are the centre of life for entire families.
          They do not switch off. Every decision, every problem, every small win flows through
          the household. The business and life are completely intertwined.
        </p>
      </FadeUp>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {[
          { t: 'Primary Owner & Decision Holder', d: 'Holds complete control over money, credit, suppliers and structural decisions. The store runs from this person’s memory. Their absence creates immediate decision paralysis.', w: 'Founder · senior family member' },
          { t: 'Supply & Operations Handler', d: 'The physical backbone, unloading, stacking, shelf order, supplier timing. Executes decisions; first to feel the impact of poor ones.', w: 'Elder son · trusted helper' },
          { t: 'Digital & Phone Based Handler', d: 'Manages UPI, QR codes, WhatsApp orders. Introduces new tools, but adoption depends on the owner’s approval, so digital gets layered on top of manual.', w: 'Son or daughter, 18 to 30s' },
          { t: 'Storefront Presence & Continuity', d: 'Keeps the store open, secure and functional through the day. No decision authority, its function is stability and presence.', w: 'Wife or mother · elder member' },
        ].map((r, i) => (
          <FadeUp key={i} delay={i * 0.07} className="group border border-ink/15 p-8 transition-colors duration-500 hover:bg-brand-orange hover:text-paper hover:border-brand-orange">
            <p className="text-xs font-medium tracking-[0.25em] opacity-50">ROLE 0{i + 1}</p>
            <p className="mt-5 text-xl font-medium tracking-tight leading-snug">{r.t}</p>
            <p className="mt-4 text-sm leading-relaxed opacity-75">{r.d}</p>
            <p className="mt-5 text-[11px] uppercase tracking-[0.2em] opacity-60">{r.w}</p>
          </FadeUp>
        ))}
      </div>
      <FadeUp delay={0.15}>
        <p className="mt-10 text-sm md:text-base text-ink/60 leading-relaxed max-w-3xl">
          The moment even one role drops out, the entire responsibility collapses back onto the
          primary decision holder. The work remains constant, only the load concentrates on one
          person, because they carry the highest risk and accountability.
        </p>
      </FadeUp>
    </section>

    {/* THE BIG GAP + SYSTEM VS */}
    <section className="bg-brand-black text-paper px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="context-gap">
      <Eyebrow color="#F9526B">The Big Gap</Eyebrow>
      <MaskedLines
        lines={['The real conflict is not', 'online vs offline.', 'It is system vs no system.']}
        className="mt-10 text-4xl md:text-7xl font-light tracking-tighter leading-[1.02]"
      />
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/15 border border-white/15">
        <FadeUp className="bg-brand-black p-8 md:p-12">
          <p className="text-xs uppercase tracking-[0.3em] text-paper/50">On one side</p>
          <p className="mt-4 text-2xl font-medium tracking-tight">Structured, data driven organisations</p>
          <ul className="mt-6 space-y-3 text-sm md:text-base text-paper/70 leading-relaxed">
            <li>Clear SOPs for every function</li>
            <li>Continuous data capture and feedback loops</li>
            <li>Technology embedded at every layer</li>
            <li>Increasing use of AI for optimization</li>
          </ul>
        </FadeUp>
        <FadeUp delay={0.1} className="bg-brand-black p-8 md:p-12">
          <p className="text-xs uppercase tracking-[0.3em] text-paper/50">On the other</p>
          <p className="mt-4 text-2xl font-medium tracking-tight">Small retail that is not truly a system</p>
          <ul className="mt-6 space-y-3 text-sm md:text-base text-paper/70 leading-relaxed">
            <li>Informal, inconsistent processes</li>
            <li>Gut driven, reactive decision making</li>
            <li>Data fragmented across memory, notebooks, people</li>
            <li>Control centralised, often a bottleneck</li>
          </ul>
        </FadeUp>
      </div>
      <FadeUp delay={0.15}>
        <p className="mt-12 text-lg md:text-2xl font-medium leading-relaxed max-w-4xl text-paper/85">
          The real challenge is not building systems. It is enabling these businesses to
          <span className="text-brand-pink"> become systems, without forcing them to stop being
          what they already are.</span>
        </p>
        <p className="mt-6 text-sm md:text-base text-paper/50 leading-relaxed max-w-3xl">
          The constraint is not affordability. It is perceived value versus perceived risk, shaped
          by behaviour: any new system must deliver clear, immediate value, feel frugal, work
          almost invisibly, and never take away control.
        </p>
      </FadeUp>
    </section>
  </ChapterShell>
);

export default Context;
